import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Plus, Warning, Printer, Lightning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { printReplenishmentSlip } from '@/lib/printSlip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReplenishmentPage({ user }) {
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pendingReplTasks, setPendingReplTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newRule, setNewRule] = useState({
    item_id: '',
    location_id: '',
    min_threshold: '',
    max_threshold: '',
    trigger_type: 'min_stock'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const [alertsRes, rulesRes, itemsRes, locationsRes, tasksRes] = await Promise.all([
        axios.post(`${API}/replenishment/check`, {}, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/replenishment-rules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/locations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tasks/details`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAlerts(alertsRes.data);
      setRules(rulesRes.data);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
      setPendingReplTasks(tasksRes.data.filter(t => t.task_type === 'replenishment' && t.status === 'pending'));
    } catch (error) {
      toast.error('Failed to load replenishment data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/replenishment-rules`, {
        ...newRule,
        min_threshold: parseInt(newRule.min_threshold),
        max_threshold: parseInt(newRule.max_threshold)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Replenishment rule added');
      setShowAddRule(false);
      setNewRule({ item_id: '', location_id: '', min_threshold: '', max_threshold: '', trigger_type: 'min_stock' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add rule');
    }
  };

  const handleGenerateAndPrint = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(`${API}/replenishment/generate-tasks`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const generated = res.data.tasks || [];
      if (generated.length === 0) {
        toast.warning('No source inventory found in other locations to replenish from');
        setGenerating(false);
        return;
      }
      const slipNo = `REPL-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Date.now().toString().slice(-4)}`;
      printReplenishmentSlip(slipNo, generated);
      toast.success(`Generated ${generated.length} replenishment task(s) - printing instructions`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate replenishment tasks');
    } finally {
      setGenerating(false);
    }
  };

  const handleCompleteReplenishment = async (taskId) => {
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/replenishment/complete/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Replenishment completed — stock moved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete replenishment');
    }
  };

  const handlePrintPending = () => {
    if (pendingReplTasks.length === 0) return;
    const slipNo = `REPL-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Date.now().toString().slice(-4)}`;
    printReplenishmentSlip(slipNo, pendingReplTasks.map(t => ({
      item_code: t.item_code,
      qc_number: t.qc_number,
      batch_number: t.batch_number,
      pallets: t.pallets || t.quantity,
      cartons: t.cartons,
      from_location_code: t.from_location?.code,
      to_location_code: t.to_location?.code,
      priority: t.priority,
    })));
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="replenishment-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="replenishment-title">
            Auto Replenishment
          </h1>
          <p className="text-[#52525B]">Monitor low stock alerts and replenishment rules</p>
        </div>
        <div className="flex gap-2">
          {(user.role === 'admin' || user.role === 'manager') && alerts.length > 0 && (
            <button
              onClick={handleGenerateAndPrint}
              disabled={generating}
              className="btn-primary flex items-center gap-2"
              data-testid="generate-replenishment-button"
            >
              <Lightning size={18} weight="fill" />
              {generating ? 'Generating...' : `Generate & Print (${alerts.length})`}
            </button>
          )}
          {(user.role === 'admin' || user.role === 'manager') && (
            <button
              onClick={() => setShowAddRule(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="add-rule-button"
            >
              <Plus size={18} weight="bold" />
              Add Rule
            </button>
          )}
        </div>
      </div>

      <div className="mb-8">
        <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Low Stock Alerts</h2>
        {alerts.length === 0 ? (
          <div className="card text-center py-8 bg-[#16A34A]/5 border-[#16A34A]/20">
            <Bell size={40} className="mx-auto text-[#16A34A] mb-3" weight="duotone" />
            <h3 className="heading text-lg font-bold text-[#09090B] mb-1">All stock levels are healthy</h3>
            <p className="text-sm text-[#52525B]">No replenishment needed at the moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert, index) => {
              const isHigh = alert.severity === 'high';
              return (
                <div
                  key={index}
                  className={`rounded-md border-2 p-4 ${isHigh ? 'bg-[#FEF2F2] border-[#DC2626]' : 'bg-[#FFFBEB] border-[#F59E0B]'}`}
                  data-testid="replenishment-alert"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isHigh ? 'bg-[#DC2626]' : 'bg-[#F59E0B]'}`}>
                      <Warning size={22} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-[10px] font-black rounded-sm uppercase tracking-wide text-white ${isHigh ? 'bg-[#DC2626]' : 'bg-[#F59E0B]'}`} data-testid="alert-severity">
                          {alert.severity} priority
                        </span>
                        <span className="mono text-sm font-bold text-[#09090B]" data-testid="alert-sku">{alert.item?.sku}</span>
                        <span className="text-sm text-[#52525B]" data-testid="alert-item-name">— {alert.item?.name}</span>
                      </div>
                      <p className="text-sm text-[#52525B] mb-3" data-testid="alert-location">
                        Target location: <span className="mono font-bold text-[#09090B]">{alert.location?.code}</span>
                      </p>
                      <div className="grid grid-cols-3 gap-6 text-sm">
                        <div>
                          <p className="text-xs text-[#52525B] uppercase tracking-wide">Current</p>
                          <p className={`mono text-2xl font-black ${isHigh ? 'text-[#DC2626]' : 'text-[#F59E0B]'}`} data-testid="alert-current-qty">{alert.current_quantity}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#52525B] uppercase tracking-wide">Min Threshold</p>
                          <p className="mono text-2xl font-black text-[#09090B]">{alert.rule?.min_threshold}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#52525B] uppercase tracking-wide">Shortfall</p>
                          <p className="mono text-2xl font-black text-[#16A34A]" data-testid="alert-required-qty">+{alert.required_quantity}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pendingReplTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading text-xl font-bold text-[#09090B]">Pending Replenishment Tasks</h2>
            <button
              onClick={handlePrintPending}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="print-pending-repl-button"
            >
              <Printer size={16} weight="bold" />
              Reprint All ({pendingReplTasks.length})
            </button>
          </div>
          <div className="space-y-3">
            {pendingReplTasks.map((task) => {
              const isHigh = task.priority === 'high';
              return (
                <div key={task.id} className="card" data-testid="repl-task-card">
                  <div className="flex items-start gap-4">
                    <div className={`w-2 self-stretch rounded-full ${isHigh ? 'bg-[#DC2626]' : 'bg-[#F59E0B]'}`}></div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className={`px-2 py-0.5 text-[10px] font-black rounded-sm uppercase tracking-wide text-white ${isHigh ? 'bg-[#DC2626]' : 'bg-[#F59E0B]'}`} data-testid="repl-task-priority">
                          {task.priority} priority
                        </span>
                        <span className="mono text-sm font-bold text-[#007AFF]" data-testid="repl-task-item">{task.item_code}</span>
                        {task.batch_number && <span className="text-xs mono text-[#52525B]">Batch: {task.batch_number}</span>}
                        {task.qc_number && <span className="text-xs mono text-[#52525B]">QC: {task.qc_number}</span>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-[#52525B] text-xs uppercase tracking-wide">Pick From</p>
                          <p className="mono font-bold text-[#F59E0B]" data-testid="repl-task-from">{task.from_location?.code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[#52525B] text-xs uppercase tracking-wide">Put At</p>
                          <p className="mono font-bold text-[#16A34A]" data-testid="repl-task-to">{task.to_location?.code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-[#52525B] text-xs uppercase tracking-wide">Quantity</p>
                          <p className="mono font-bold text-[#09090B]" data-testid="repl-task-qty">{task.pallets || task.quantity} plts</p>
                        </div>
                        <div className="flex items-end justify-end">
                          <button
                            onClick={() => handleCompleteReplenishment(task.id)}
                            className="btn-primary text-xs px-3 py-1.5"
                            data-testid="repl-task-complete-button"
                          >
                            Mark Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Replenishment Rules</h2>
        {rules.length === 0 ? (
          <div className="card text-center py-8">
            <Bell size={40} className="mx-auto text-[#52525B] mb-3" weight="duotone" />
            <h3 className="heading text-lg font-bold text-[#09090B] mb-1">No rules configured</h3>
            <p className="text-sm text-[#52525B]">Add replenishment rules to automate stock monitoring</p>
          </div>
        ) : (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="rules-table">
                <thead>
                  <tr className="border-b border-[#E4E4E7]">
                    <th className="text-left p-3 text-sm font-medium text-[#52525B]">Item</th>
                    <th className="text-left p-3 text-sm font-medium text-[#52525B]">Location</th>
                    <th className="text-left p-3 text-sm font-medium text-[#52525B]">Min Threshold</th>
                    <th className="text-left p-3 text-sm font-medium text-[#52525B]">Max Threshold</th>
                    <th className="text-left p-3 text-sm font-medium text-[#52525B]">Trigger Type</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => {
                    const item = items.find(i => i.id === rule.item_id);
                    const location = locations.find(l => l.id === rule.location_id);
                    return (
                      <tr key={rule.id} className="border-b border-[#E4E4E7] even:bg-[#FAFAFA]" data-testid="rule-row">
                        <td className="p-3" data-testid="rule-item">
                          <p className="mono text-sm font-bold text-[#09090B]">{item?.sku}</p>
                          <p className="text-xs text-[#52525B]">{item?.name}</p>
                        </td>
                        <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="rule-location">{location?.code}</td>
                        <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="rule-min">{rule.min_threshold}</td>
                        <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="rule-max">{rule.max_threshold}</td>
                        <td className="p-3 text-sm capitalize text-[#52525B]" data-testid="rule-trigger">{rule.trigger_type.replace('_', ' ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
        <DialogContent data-testid="add-rule-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Add Replenishment Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRule} className="space-y-4">
            <div>
              <Label htmlFor="item">Item *</Label>
              <Select value={newRule.item_id} onValueChange={(value) => setNewRule({ ...newRule, item_id: value })}>
                <SelectTrigger data-testid="add-rule-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.id} value={item.id}>{item.sku} - {item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="location">Location *</Label>
              <Select value={newRule.location_id} onValueChange={(value) => setNewRule({ ...newRule, location_id: value })}>
                <SelectTrigger data-testid="add-rule-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>{location.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="min">Min Threshold *</Label>
                <Input
                  id="min"
                  type="number"
                  value={newRule.min_threshold}
                  onChange={(e) => setNewRule({ ...newRule, min_threshold: e.target.value })}
                  placeholder="10"
                  required
                  data-testid="add-rule-min-input"
                />
              </div>
              <div>
                <Label htmlFor="max">Max Threshold *</Label>
                <Input
                  id="max"
                  type="number"
                  value={newRule.max_threshold}
                  onChange={(e) => setNewRule({ ...newRule, max_threshold: e.target.value })}
                  placeholder="100"
                  required
                  data-testid="add-rule-max-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="trigger">Trigger Type *</Label>
              <Select value={newRule.trigger_type} onValueChange={(value) => setNewRule({ ...newRule, trigger_type: value })}>
                <SelectTrigger data-testid="add-rule-trigger-select">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="min_stock">Minimum Stock</SelectItem>
                  <SelectItem value="zone_based">Zone Based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="submit" className="flex-1 btn-primary" data-testid="add-rule-submit-button">
                Add Rule
              </button>
              <button type="button" onClick={() => setShowAddRule(false)} className="flex-1 btn-secondary" data-testid="add-rule-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
